import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Save, X, Loader, CreditCard, Eye, Receipt, Check, Lock, LockOpen,
  Archive, ArchiveRestore,
} from 'lucide-react';
import { toast } from 'sonner';

import { useCustomer } from '../hooks/useCustomer';
import { useCustomFields } from '../hooks/useCustomFields';
import { getCustomerSchema } from '../schemas/customerSchema';
import apiClient from '../utils/apiClient';
import customersService from '../services/customersService';
import { useFacility } from '../context/FacilityContext';
import { useAuth } from '../context/AuthContext';

import PageBackLink from '../components/PageBackLink';
import PersonalSection from '../components/forms/PersonalSection';
import CustomFieldsSection from '../components/forms/CustomFieldsSection';
import ArraySection from '../components/forms/ArraySection';
import VehicleCard from '../components/forms/VehicleCard';
import BoatCard from '../components/forms/BoatCard';
import DocumentsSection from '../components/forms/DocumentsSection';
import RentalsInlineSection from '../components/forms/RentalsInlineSection';
import CustomerNotesSection from '../components/forms/CustomerNotesSection';
import BillingHistorySection from '../components/forms/BillingHistorySection';
import AutopaySection from '../components/forms/AutopaySection';
import CustomerHistory from './CustomerHistory';
import EmailTab from './customer/EmailTab';
import TextTab from './customer/TextTab';
import CustomerCreditBadge from '../components/CustomerCreditBadge';

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  // /customers/:id (no /edit) = READ-ONLY profile view: the same tabbed
  // record, but Basic Info fields are disabled and Save is hidden — no
  // accidental profile edits from a lookup. Comms/billing/rentals tabs
  // stay fully interactive. "Edit customer" switches to /edit.
  const routerLocation = useLocation();
  const readOnly = isEditMode && !routerLocation.pathname.endsWith('/edit');

  const { getCustomer, createCustomer, updateCustomer, loading: customerLoading } = useCustomer();
  const { fields: customFields, fetchFields, loading: fieldsLoading } = useCustomFields();
  const { currentFacility } = useFacility();
  const { user, accessToken } = useAuth();
  const [startingImpersonation, setStartingImpersonation] = useState(false);

  const [activeTab, setActiveTab] = useState('basic');
  const [initialLoading, setInitialLoading] = useState(true);
  // Inline "Saved!" confirmation pill — appears in the action cluster
  // for ~2.5s after a successful save so the operator stays on the
  // record instead of getting bounced to the list. Cleared on unmount.
  const [savedAt, setSavedAt] = useState(null);
  const savedTimerRef = useRef(null);
  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);
  // Derived rollup fields from GET /customers/{id} — surface in the header
  // so the operator sees Active/Delinquent state on the customer page.
  const [derivedStatus, setDerivedStatus] = useState(null);
  const [rentalCount, setRentalCount] = useState(0);
  // Inline rentals list (ESS parity). The GET /customers/{id} endpoint
  // denormalizes the per-rental unit join server-side so this is render-only.
  const [rentals, setRentals] = useState([]);
  // Money owed BACK to this customer. It arrives from an overpayment, a
  // transfer downgrade, or a move-out — and appeared nowhere in the app, so
  // the operator had no way to know it was there, let alone return it.
  const [creditBalance, setCreditBalance] = useState(0);
  const [gateCode, setGateCode] = useState('');
  // collection_stage is the single stored trigger (current|past_due|locked_out),
  // computed server-side from days-past-due + the facility threshold + a manual
  // override and persisted on the record. `lockoutReason` (manual|delinquency)
  // says which driver set it; `gateLockoutManual` is the stored operator
  // override flag that pins the stage (drives the button + its label).
  const [collectionStage, setCollectionStage] = useState('current');
  // Autopay checkboxes (Billing tab): enabling needs the tenant's saved
  // card — the server guards it; this flag drives the disabled-with-hint
  // state so the operator learns WHY instead of hitting an error.
  const [hasSavedMethod, setHasSavedMethod] = useState(false);
  // Archive lives on the RECORD view too (owner, 2026-08-01): after a
  // move-out the operator is standing right here — sending them back to
  // the list to find the archive action made "step 2" feel missing.
  const [isArchived, setIsArchived] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [lockoutReason, setLockoutReason] = useState(null);
  const [gateLockoutManual, setGateLockoutManual] = useState(false);
  const [gateLockBusy, setGateLockBusy] = useState(false);
  // Note count for the "Notes (N)" tab label. CustomerNotesSection feeds
  // this back via onCountChange whenever it refreshes its list.
  const [noteCount, setNoteCount] = useState(0);
  // Inline billing history (ESS parity, third inline section). The GET
  // /customers/{id} endpoint merges invoices + payments + refunds + voids
  // and stamps a running balance per row server-side. Capped at 50 rows;
  // billingHistoryTruncated flag drives the "Full history (N)" toggle.
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingHistoryTotal, setBillingHistoryTotal] = useState(0);
  const [billingHistoryTruncated, setBillingHistoryTruncated] = useState(false);

  const handleArchiveToggle = async () => {
    if (!currentFacility?._id || !id || archiveBusy) return;
    if (!isArchived && !window.confirm(
      'Archive this customer? They are hidden from the default customer '
      + 'list. Nothing is deleted — you can unarchive at any time.')) return;
    setArchiveBusy(true);
    try {
      if (isArchived) {
        await customersService.unarchive(currentFacility._id, id);
        setIsArchived(false);
        toast.success('Customer unarchived.');
      } else {
        // Server guards reject archiving with an active rental or open
        // invoices — surface its reason verbatim.
        await customersService.archive(currentFacility._id, id);
        setIsArchived(true);
        toast.success('Customer archived.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not change the archive state.');
    } finally {
      setArchiveBusy(false);
    }
  };

  const methods = useForm({
    resolver: zodResolver(getCustomerSchema(customFields)),
    defaultValues: {
      full_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      vehicles: [],
      boats: [],
      trailers: [],
      rvs: [],
      custom_fields: []
    }
  });

  const { handleSubmit, reset, formState: { isSubmitting, errors } } = methods;

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchFields();
        if (isEditMode) {
          const customer = await getCustomer(id);
          // Cross-facility guard: if the operator switched facilities
          // while this customer detail page was already open, the URL
          // still points at the OLD facility's customer. Loading that
          // record would surface someone else's tenant under the new
          // facility's header -- confusing and a privacy concern.
          // Bounce back to the new facility's customer list instead.
          if (currentFacility?._id
              && customer?.facility_id
              && customer.facility_id !== currentFacility._id) {
            console.warn('[CustomerForm] customer belongs to a different facility', {
              customerFacility: customer.facility_id,
              currentFacility: currentFacility._id,
            });
            navigate('/customers', { replace: true });
            return;
          }
          // Coerce the list fields by TYPE, not truthiness. A record whose
          // custom_fields arrived as an OBJECT (a one-off script wrote a
          // QuickBooks id with dot-notation into a missing field, so Mongo
          // made a dict) passed `|| []` straight through to Zod, which
          // failed the WHOLE form — blocking every unrelated save on that
          // customer, gate code included. Shape drift in one field must
          // never lock an operator out of the record.
          const asList = (v) => (Array.isArray(v) ? v : []);
          reset({
            ...customer,
            vehicles: asList(customer.vehicles),
            boats: asList(customer.boats),
            trailers: asList(customer.trailers),
            rvs: asList(customer.rvs),
            custom_fields: asList(customer.custom_fields)
          });
          setDerivedStatus(customer.derived_status || null);
          setRentalCount(customer.rental_count || 0);
          setRentals(Array.isArray(customer.rentals) ? customer.rentals : []);
          setGateCode(customer.gate_code || '');
          setCollectionStage(customer.collection_stage || 'current');
          setHasSavedMethod(Boolean(
            customer.stripe_customer_id && customer.autopay_payment_method));
          setIsArchived(Boolean(customer.is_archived));
          setLockoutReason(customer.lockout_reason || null);
          setGateLockoutManual(Boolean(customer.gate_locked_out));
          // Credit used to load only via the Full-history toggle, so the
          // Credit badge never showed on a normal visit.
          setCreditBalance(Number(customer.credit_balance || 0));
          setBillingHistory(
            Array.isArray(customer.billing_history) ? customer.billing_history : []
          );
          setBillingHistoryTotal(Number(customer.billing_history_total || 0));
          setBillingHistoryTruncated(Boolean(customer.billing_history_truncated));
        }
      } catch (err) {
        console.error("Failed to load form data", err);
      } finally {
        setInitialLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode, currentFacility?._id]);

  // Smoothly re-pull the billing ledger + rentals after a void (or other
  // ledger mutation) so the section updates in place — no full page reload.
  const refreshBilling = useCallback(async () => {
    if (!isEditMode || !id) return;
    try {
      const customer = await getCustomer(id);
      if (!customer) return;
      setBillingHistory(
        Array.isArray(customer.billing_history) ? customer.billing_history : []
      );
      setBillingHistoryTotal(Number(customer.billing_history_total || 0));
      setBillingHistoryTruncated(Boolean(customer.billing_history_truncated));
      setRentals(Array.isArray(customer.rentals) ? customer.rentals : []);
      setDerivedStatus(customer.derived_status || null);
      setCreditBalance(Number(customer.credit_balance || 0));
    } catch (err) {
      console.error('Failed to refresh billing history', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  // Pull the UNCAPPED ledger (server skips the 50-row cap) — used by the
  // Billing section's "Full history" toggle when the list was truncated.
  const loadFullHistory = useCallback(async () => {
    if (!isEditMode || !id || !currentFacility?._id) return;
    try {
      const res = await apiClient.get(
        `/api/facilities/${currentFacility._id}/customers/${id}?full_history=true`,
      );
      const customer = res.data;
      setCreditBalance(Number(customer.credit_balance || 0));
      setBillingHistory(
        Array.isArray(customer.billing_history) ? customer.billing_history : []
      );
      setBillingHistoryTotal(Number(customer.billing_history_total || 0));
      setBillingHistoryTruncated(false);
    } catch (err) {
      console.error('Failed to load full history', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode, currentFacility?._id]);

  // Toggle the manual gate lockout. Sets the stored gate_locked_out state
  // (reason="manual"); the gate integration enforces it on its next sync.
  const handleGateLockToggle = async () => {
    if (!id || !currentFacility?._id) return;
    setGateLockBusy(true);
    try {
      if (gateLockoutManual) {
        await customersService.gateUnlock(currentFacility._id, id);
      } else {
        await customersService.gateLock(currentFacility._id, id);
      }
      // Re-read the live state: a delinquency lockout can persist after a
      // manual unlock (payment, not the button, clears that).
      const fresh = (await customersService.getById(currentFacility._id, id)).data;
      setCollectionStage(fresh.collection_stage || 'current');
      setLockoutReason(fresh.lockout_reason || null);
      setGateLockoutManual(Boolean(fresh.gate_locked_out));
    } catch (err) {
      console.error('Gate lock toggle failed', err);
    } finally {
      setGateLockBusy(false);
    }
  };

  // "View as tenant" -- mint a 15-min impersonation token, stash a small
  // session record so the portal-side banner can render, then open the
  // portal in a new tab with the token in sessionStorage. Audit log
  // captures every action under the operator's identity automatically.
  const handleImpersonate = async () => {
    if (!isEditMode || !currentFacility?._id) return;
    setStartingImpersonation(true);
    try {
      const res = await apiClient.post(
        `/api/facilities/${currentFacility._id}/customers/${id}/impersonate`,
        { reason: null },
      );
      const { token, session_id, customer, expires_at } = res.data;
      const sessionPayload = {
        session_id,
        token,
        facility_id: currentFacility._id,
        customer_id: customer?.id || id,
        customer_name: customer?.full_name,
        customer_email: customer?.email,
        operator_email: user?.email,
        operator_access_token: accessToken,
        expires_at,
      };
      // Banner reads this on every portal page; portal API calls read
      // the token out of tenantAccessToken (existing convention).
      localStorage.setItem('tenantImpersonation', JSON.stringify(sessionPayload));
      localStorage.setItem('tenantAccessToken', token);
      window.open('/portal/dashboard', '_blank', 'noopener');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === 'string'
        ? `Could not start impersonation: ${detail}`
        : 'Could not start impersonation.');
    } finally {
      setStartingImpersonation(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (isEditMode) {
        await updateCustomer(id, data);
        // Stay on the record. Surface inline confirmation in the action
        // cluster; auto-dismiss the pill so it doesn't linger.
        setSavedAt(Date.now());
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSavedAt(null), 2500);
      } else {
        // New customer → land the operator on the edit view of the
        // record they just created instead of the list, so they can
        // keep working (add rentals, notes, etc.).
        const created = await createCustomer(data);
        const newId = created?._id || created?.id;
        if (newId) {
          navigate(`/customers/${newId}/edit`, { replace: true });
        } else {
          // Backend didn't return an id — fall back to the list rather
          // than stranding the operator on a stale form.
          navigate('/customers');
        }
      }
    } catch (err) {
      console.error(err);
      // Surface the real backend error instead of a generic catch-all.
      // FastAPI returns Pydantic 422 errors as an array of
      // {loc, msg, type}; other handlers return a string. Format both.
      const detail = err?.response?.data?.detail;
      let msg = 'Failed to save customer.';
      if (typeof detail === 'string') {
        msg = `Failed to save customer: ${detail}`;
      } else if (Array.isArray(detail)) {
        const fieldErrors = detail.map((e) => {
          const loc = Array.isArray(e.loc)
            ? e.loc.filter((x) => x !== 'body').join('.')
            : e.loc;
          return `${loc}: ${e.msg}`;
        }).join('\n');
        msg = `Failed to save customer:\n${fieldErrors}`;
      }
      alert(msg);
    }
  };

  if (initialLoading || fieldsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'basic', name: 'Basic Info' },
    // Rentals tab only makes sense on existing customers; rental rows live
    // on a separate collection that doesn't get written until the wizard
    // completes anyway.
    ...(isEditMode ? [{ id: 'rentals', name: `Rentals${rentalCount > 0 ? ` (${rentalCount})` : ''}` }] : []),
    // History tab — broad timeline (rentals/invoices/payments/notes
    // synthesized from facts). Sits BEFORE Billing because Billing is the
    // narrower money-only slice; History is the full story.
    ...(isEditMode ? [{ id: 'history', name: 'History' }] : []),
    ...(isEditMode ? [{ id: 'notes', name: `Notes${noteCount > 0 ? ` (${noteCount})` : ''}` }] : []),
    // Billing history denormalized onto the customer GET — only meaningful
    // for persisted customers, label counts when there are entries.
    ...(isEditMode
      ? [{ id: 'billing', name: `Billing${billingHistoryTotal > 0 ? ` (${billingHistoryTotal})` : ''}` }]
      : []),
    { id: 'assets', name: 'Assets & Vehicles' },
    { id: 'custom', name: 'Additional Info' },
    // Uploads need a persisted customer to attach to.
    ...(isEditMode ? [{ id: 'documents', name: 'Documents' }] : []),
    // Email + Text history + one-off send (added 2026-06-05). Both
    // need a persisted customer to scope their feed and to have an
    // email/phone on file before send.
    ...(isEditMode ? [{ id: 'email', name: 'Email' }] : []),
    ...(isEditMode ? [{ id: 'sms', name: 'Text' }] : []),
  ];

  return (
    <FormProvider {...methods}>
      <div>
        {isEditMode && (
          <PageBackLink to="/customers" label="Customers" />
        )}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl font-semibold text-slate-900">
                {isEditMode ? 'Edit Customer' : 'New Customer'}
              </h1>
              {isEditMode && derivedStatus === 'Delinquent' && (
                <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-200">Delinquent</span>
              )}
              {isEditMode && derivedStatus === 'Active' && (
                <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
              )}
              {isEditMode && derivedStatus === 'Ended' && (
                <span className="px-2.5 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200">Ended</span>
              )}
              {isEditMode && derivedStatus === 'None' && (
                <span className="px-2.5 py-0.5 text-[11px] font-medium rounded-full bg-slate-50 text-slate-500 border border-slate-200">No rentals</span>
              )}
              {isEditMode && collectionStage === 'locked_out' && (
                <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Locked Out{lockoutReason === 'manual' ? ' · manual' : ''}
                </span>
              )}
              {isEditMode && collectionStage === 'past_due' && (
                <span className="px-2.5 py-0.5 text-[11px] font-medium rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
                  Past Due
                </span>
              )}
              {isEditMode && isArchived && (
                <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-600 border border-slate-200 inline-flex items-center gap-1">
                  <Archive className="h-3 w-3" /> Archived
                </span>
              )}
            </div>
            <p className="text-[13px] text-slate-400 mt-0.5">
              {isEditMode
                ? `Update customer information${rentalCount > 0 ? ` · ${rentalCount} rental${rentalCount > 1 ? 's' : ''}` : ''}`
                : 'Add a new customer to the system'}
            </p>
          </div>
          {isEditMode && (
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {savedAt && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[12px] font-medium"
                  role="status"
                  aria-live="polite"
                >
                  <Check className="h-3.5 w-3.5" /> Saved
                </span>
              )}
              <CustomerCreditBadge
                facilityId={currentFacility?._id}
                customerId={id}
                credit={creditBalance}
                onChanged={loadFullHistory}
              />
              {/* Archive / Unarchive on the RECORD view — after a move-out
                  the operator is HERE, and step 2 (archive) shouldn't
                  require a trip back to the list. Server guards still
                  block archiving with an active rental / open invoices. */}
              <button
                type="button"
                onClick={handleArchiveToggle}
                disabled={archiveBusy}
                title={isArchived
                  ? 'Restore this customer to the active list'
                  : 'Hide this customer from the default list (nothing is deleted)'}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg font-medium text-white transition-colors disabled:opacity-50 ${isArchived
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {isArchived
                  ? <ArchiveRestore className="h-3.5 w-3.5" />
                  : <Archive className="h-3.5 w-3.5" />}
                {archiveBusy ? '...' : (isArchived ? 'Unarchive' : 'Archive')}
              </button>
              <button
                type="button"
                onClick={handleImpersonate}
                disabled={startingImpersonation}
                title="Open the tenant's portal as them -- audit-logged, 15 min"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
              >
                <Eye className="h-3.5 w-3.5" />
                {startingImpersonation ? 'Starting...' : 'View as tenant'}
              </button>
              {/* Gate Lock / Restore -- toggles the stored gate_locked_out
                  state. Amber when active (lock), emerald to restore. The
                  configured gate integration enforces it on next sync. */}
              <button
                type="button"
                onClick={handleGateLockToggle}
                disabled={gateLockBusy}
                title={gateLockoutManual
                  ? "Clear the manual gate lockout"
                  : 'Manually lock this tenant out of gate access'}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg font-medium text-white transition-colors disabled:opacity-50 ${gateLockoutManual
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-amber-500 hover:bg-amber-600'}`}
              >
                {gateLockoutManual
                  ? <LockOpen className="h-3.5 w-3.5" />
                  : <Lock className="h-3.5 w-3.5" />}
                {gateLockBusy ? '...' : (gateLockoutManual ? 'Restore Gate' : 'Lock Out')}
              </button>
              {/* Record Payment -- after-the-fact ledger entry (check,
                  cash, off-system swipe). Mirrors the Payments page
                  "Record Payment" button and pre-selects this customer
                  via query param. Outlined secondary so the brand-
                  outline hijack in index.css picks it up. */}
              <button
                type="button"
                onClick={() => navigate(`/payments/new?customer_id=${id}`)}
                title="Record a payment collected outside mySMS360 (check, cash, external terminal)"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium transition-colors"
              >
                <Receipt className="h-3.5 w-3.5" /> Record Payment
              </button>
              {/* Take Payment -- live Stripe Elements card-entry flow for
                  phone payments. Primary brand button so it flips with
                  the brand axis (bg-blue-600 is hijacked → --c-brand). */}
              <button
                type="button"
                onClick={() => navigate(`/payments/take?customer_id=${id}`)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
              >
                <CreditCard className="h-3.5 w-3.5" /> Take Payment
              </button>
            </div>
          )}
        </div>

        {Object.keys(errors).length > 0 && (
          <div className="mb-4 rounded-xl border border-red-200/80 bg-red-50 p-4">
            <h3 className="text-[13px] font-medium text-red-700 mb-1">There are errors in the form</h3>
            <ul className="text-[13px] text-red-600 list-disc pl-5 space-y-0.5">
              {Object.entries(errors).slice(0, 3).map(([key, error]) => (
                <li key={key}>{key}: {error.message || 'Invalid value'}</li>
              ))}
              {Object.keys(errors).length > 3 && <li>...and more</li>}
            </ul>
          </div>
        )}

        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <div className="border-b border-slate-100 px-1">
            <nav className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-5">
            <div className={activeTab === 'basic' ? 'block' : 'hidden'}>
              {readOnly && (
                <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200/80 bg-blue-50/60 px-4 py-2.5">
                  <span className="text-[13px] text-slate-700 font-medium">Viewing profile (read-only)</span>
                  <button
                    type="button"
                    onClick={() => navigate(`/customers/${id}/edit`)}
                    className="rounded-lg bg-blue-600 text-white text-[12px] font-medium px-3.5 py-1.5 hover:bg-blue-700"
                  >
                    Edit customer
                  </button>
                </div>
              )}
              <fieldset disabled={readOnly} className={readOnly ? 'opacity-90' : ''}>
                <PersonalSection />
              </fieldset>
            </div>

            {isEditMode && (
              <div className={activeTab === 'rentals' ? 'block' : 'hidden'}>
                <RentalsInlineSection
                  rentals={rentals}
                  gateCode={gateCode}
                  facilityId={currentFacility?._id}
                  onChanged={refreshBilling}
                />
              </div>
            )}

            {isEditMode && (
              <div className={activeTab === 'history' ? 'block' : 'hidden'}>
                <CustomerHistory customerId={id} />
              </div>
            )}

            <div className={activeTab === 'assets' ? 'block' : 'hidden'}>
              <ArraySection
                title="Vehicles"
                description="Manage customer vehicles."
                name="vehicles"
                itemSchema={{ vehicle_year: new Date().getFullYear(), vehicle_make: '', vehicle_model: '' }}
                renderItem={(props) => <VehicleCard {...props} />}
              />
              <div className="h-5"></div>
              <ArraySection
                title="Boats"
                description="Manage customer boats."
                name="boats"
                itemSchema={{ boat_year: new Date().getFullYear(), boat_make: '', boat_model: '' }}
                renderItem={(props) => <BoatCard {...props} />}
              />
            </div>

            <div className={activeTab === 'custom' ? 'block' : 'hidden'}>
              <CustomFieldsSection customFieldDefinitions={customFields} />
              {customFields.length === 0 && (
                <div className="text-center py-8 text-[13px] text-slate-400">
                  No custom fields defined. Go to Settings to add fields.
                </div>
              )}
            </div>

            {isEditMode && (
              <div className={activeTab === 'documents' ? 'block' : 'hidden'}>
                <DocumentsSection customerId={id} facilityId={currentFacility?._id} />
              </div>
            )}

            {isEditMode && (
              <div className={activeTab === 'notes' ? 'block' : 'hidden'}>
                <CustomerNotesSection customerId={id} onCountChange={setNoteCount} />
              </div>
            )}

            {isEditMode && (
              <div className={activeTab === 'billing' ? 'block' : 'hidden'}>
                <AutopaySection
                  facilityId={currentFacility?._id}
                  customerId={id}
                  rentals={rentals}
                  hasSavedMethod={hasSavedMethod}
                  onChanged={loadFullHistory}
                />
                <BillingHistorySection
                  billingHistory={billingHistory}
                  truncated={billingHistoryTruncated}
                  total={billingHistoryTotal}
                  facilityId={currentFacility?._id}
                  onChanged={refreshBilling}
                  onLoadFull={loadFullHistory}
                  customerId={id}
                  creditBalance={creditBalance}
                />
              </div>
            )}

            {isEditMode && (
              <div className={activeTab === 'email' ? 'block' : 'hidden'}>
                <EmailTab customerId={id} facilityId={currentFacility?._id} />
              </div>
            )}

            {isEditMode && (
              <div className={activeTab === 'sms' ? 'block' : 'hidden'}>
                <TextTab customerId={id} facilityId={currentFacility?._id} />
              </div>
            )}

            {!readOnly && (<div className="flex justify-end gap-2 pt-5 border-t border-slate-100 mt-5">
              <button
                type="button"
                onClick={() => navigate('/customers')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || customerLoading}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <><Loader className="h-3.5 w-3.5 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-3.5 w-3.5" /> Save Customer</>
                )}
              </button>
            </div>)}
          </form>
        </div>
      </div>
    </FormProvider>
  );
};

export default CustomerForm;
